/**
 * ProtectedRoute Component
 *
 * Route wrapper for React Router that requires authentication.
 *
 * Features:
 * - Uses React Router's Navigate for redirects
 * - Preserves location state for post-login redirect
 * - Shows loading state during auth check
 * - Type-safe with TypeScript
 *
 * Usage with React Router v6:
 * ```tsx
 * import { BrowserRouter, Routes, Route } from 'react-router-dom'
 *
 * function App() {
 *   return (
 *     <BrowserRouter>
 *       <Routes>
 *         <Route path="/login" element={<LoginPage />} />
 *         <Route
 *           path="/dashboard"
 *           element={
 *             <ProtectedRoute>
 *               <Dashboard />
 *             </ProtectedRoute>
 *           }
 *         />
 *       </Routes>
 *     </BrowserRouter>
 *   )
 * }
 * ```
 */

import { ReactNode, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useAuthStore } from '@/stores/authStore'

export interface ProtectedRouteProps {
  /**
   * The component/content to render if user is authenticated
   */
  children: ReactNode

  /**
   * Custom loading fallback (optional)
   * If not provided, shows default spinner
   */
  fallback?: ReactNode

  /**
   * Path to redirect to if not authenticated
   * @default "/login"
   */
  redirectTo?: string
}

/**
 * ProtectedRoute Component
 *
 * Wraps React Router routes to require authentication.
 * Uses React Router's Navigate component for redirects.
 */
export function ProtectedRoute({
  children,
  fallback,
  redirectTo = '/',
}: ProtectedRouteProps) {
  const { user, loading, initialized, initialize } = useAuthStore()
  const location = useLocation()
  const [isInitializing, setIsInitializing] = useState(!initialized)

  // Initialize auth store on mount if not already initialized
  useEffect(() => {
    if (!initialized) {
      initialize()
      setIsInitializing(false)
    }
  }, [initialized, initialize])

  // Show loading state while checking authentication
  if (loading || isInitializing) {
    if (fallback) {
      return <>{fallback}</>
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary">
          Έλεγχος πρόσβασης...
        </Typography>
      </Box>
    )
  }

  // Redirect to login if not authenticated
  // Preserve current location in state so we can redirect back after login
  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // User is authenticated, render protected content
  return <>{children}</>
}

/**
 * RequireAuth Component (alias)
 *
 * Alternative name for better readability in some contexts.
 *
 * Usage:
 * ```tsx
 * <RequireAuth>
 *   <ProtectedComponent />
 * </RequireAuth>
 * ```
 */
export const RequireAuth = ProtectedRoute
